/**
 * AskInstructorButton
 *
 * "Ask a question" button + form dialog for the course player. Renders
 * nothing unless instructor messaging is enabled for the course (admin
 * configures this in Course Settings → Basics).
 *
 * Submit opens the learner's default mail client via a `mailto:` URL
 * with the subject + body pre-filled. No server-side send required.
 * Easy to swap for a backend `POST /api/messages/instructor` when one
 * exists — only the submit handler changes.
 */

import { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '../ui';
import { CloseIcon, HelpOutlineIcon } from '../icons';

interface AskInstructorButtonProps {
  /** Whether instructor messaging is enabled on the course. */
  enabled?: boolean;
  /** Recipient address for the question email. */
  email?: string;
  courseTitle?: string;
  /** Optional sx override for the trigger button. */
  buttonSx?: any;
  /** Optional learner email — used to set Reply-To-friendly subject. */
  learnerName?: string;
}

export default function AskInstructorButton({
  enabled,
  email,
  courseTitle,
  buttonSx,
  learnerName,
}: AskInstructorButtonProps) {
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');

  // Feature gate — don't render the button at all if the admin hasn't
  // enabled the feature on this course (or hasn't set an email).
  if (!enabled || !email) return null;

  const defaultSubject = courseTitle ? `Question about ${courseTitle}` : 'Course question';

  const handleOpen = () => {
    setSubject(defaultSubject);
    setMessage('');
    setOpen(true);
  };

  const handleSubmit = () => {
    const trimmedSubject = subject.trim() || defaultSubject;
    const trimmedBody = message.trim();
    // mailto: encodes via standard URL escape; newlines become %0A.
    const params = new URLSearchParams();
    params.set('subject', trimmedSubject);
    if (trimmedBody) params.set('body', trimmedBody);
    // Some mail clients (Outlook web) require the raw `?` form rather
    // than URLSearchParams' encoding of '=' inside the query.
    const url = `mailto:${encodeURIComponent(email)}?${params.toString()}`;
    window.location.href = url;
    setOpen(false);
  };

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={<HelpOutlineIcon />}
        onClick={handleOpen}
        sx={{
          color: 'text.primary',
          borderColor: 'rgba(0, 0, 0, 0.23)',
          '&:hover': { borderColor: 'rgba(0, 0, 0, 0.5)', bgcolor: 'action.hover' },
          ...buttonSx,
        }}
      >
        Ask a question
      </Button>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Ask a question
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Your question goes to the instructor at <strong>{email}</strong>.
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              fullWidth
              size="small"
              autoFocus
            />
            <TextField
              label="Message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              fullWidth
              multiline
              rows={6}
              placeholder={
                learnerName
                  ? `Hi — ${learnerName} here. I have a question about the course...`
                  : 'Type your question here...'
              }
            />
            <Typography variant="caption" color="text.secondary">
              Submitting opens your default email app with this message pre-filled.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={!subject.trim() && !message.trim()}
          >
            Compose email
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
